import React, {ReactElement, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useDebouncedCallback} from 'use-debounce';
import ColorSelectCompact from '../../../../../incase-domain/components/select/compact/color/colorSelectCompact/ColorSelectCompact';
import {IElementStaticTableV2} from '../../../../../incase-domain/elements/staticTableV2';
import {setDebounceChangeHandler} from '../../../../../store/actions/tiptapEditor.actions';
import {AppState} from '../../../../../store/reducer';
import {editorSelector} from '../../../../../store/selectors/tiptapEditor.selectors';
import SectionTitle from '../../../../elements/common/SectionTitle';
import Tiptap from '../../../../elements/tipTap/TipTap';
import {TABS} from '../../../form/part/tabs';
import AnimationForm from '../../common/animationForm';
import ElementShadow from '../../common/elementShadow';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../../utils/hooks/slideUpdates/useSetSlideElement';

interface HighlightedStaticTableElementComponentProps {
    activeTab: string;
}

function HighlightedStaticTableV2ElementComponent(props: HighlightedStaticTableElementComponentProps): ReactElement {
    const {activeTab} = props;
    const highlightedElement = useHighlightedElement() as IElementStaticTableV2;
    const {id} = highlightedElement;
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const updateHighlightedElement = useSetHighlightedElement();

    const debouncedChangeHandler = useDebouncedCallback((e) => updateHighlightedElement.single('text', e), 250);

    const editor = useSelector((state: AppState) => editorSelector(state, id));
    useEffect(() => {
        if (editor) {
            dispatch(setDebounceChangeHandler(id, debouncedChangeHandler));
        }
    }, [editor, dispatch, debouncedChangeHandler, id]);

    const selectionKey = (): string => {
        if (!editor) return '';
        const {state} = editor;
        const {selection} = state;

        return `${selection.from}-${selection.to}`;
    };

    const getCurrentCellBackgroundColor = (): null | string => {
        if (!editor) return null;
        const {state} = editor;
        const {selection} = state;
        let bgColor: string | null = null;

        state.doc.nodesBetween(selection.from, selection.to, (node) => {
            if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
                if (node.attrs.backgroundColor) {
                    bgColor = node.attrs.backgroundColor;
                }
            }
        });

        return bgColor;
    };

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
                <AnimationForm
                    appearAfter={highlightedElement.appearAfter || 0}
                    elementAnimations={highlightedElement.elementAnimations}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </>
        );
    }

    return (
        <>
            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.style')} />
                <div className=" u-flex u-flex-col u-gap-3" key={selectionKey()}>
                    <Tiptap id={id} />

                    {editor && (
                        <ColorSelectCompact
                            label={t(`designSidepanel.backgroundColor`)}
                            color={getCurrentCellBackgroundColor()}
                            onChange={(color) => {
                                if (color) {
                                    editor.chain().focus().setTableCellBackgroundColor(color).run();
                                } else {
                                    editor.chain().focus().unsetTableCellBackgroundColor().run();
                                }
                            }}
                        />
                    )}
                </div>
            </div>
        </>
    );
}

export default HighlightedStaticTableV2ElementComponent;
